import moment from "moment";
import {
  CURRENCY,
  notProtectedRoute,
  permissionRoute,
  POST_TYPE,
} from "../constant/constants";
import axios from "axios";
import { toast } from "react-toastify";
import { formRegEx, formRegExReplacer } from "constant/regex";
import classNames from "classnames";
import { routes } from "@/routes";
import { Country } from "country-state-city";
import { consumerQuestions, producerQuestions } from "@/data/data";

export const apiHeader = (token, isFormData) => {
  const headers = {
    "ngrok-skip-browser-warning": "69420",
    "timezone-offset": moment().utcOffset(),
    "Content-Type": isFormData ? "multipart/form-data" : "application/json",
  };

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  return { headers };
};

export const formatMessageDate = (date) => {
  const messageDate = moment(date);
  const now = moment();

  if (messageDate.isSame(now, "minute")) {
    // If the date is exactly now, show the current time, e.g., 'HH:mm A'
    return now.format("HH:mm A");
  } else if (messageDate.isSame(now, "day")) {
    // If the date is today, use fromNow()
    return messageDate.fromNow();
  } else if (messageDate.isSame(now.subtract(1, "day"), "day")) {
    // If the date is yesterday, use fromNow()
    return messageDate.fromNow();
  } else {
    // Otherwise, show the date in a standard format, e.g., 'DD/MM/YYYY'
    return messageDate.format("DD/MM/YYYY");
  }
};

export const formatDate = (date) => {
  const formattedDate = moment(date);
  return formattedDate.format("DD/MM/YYYY");
};

export const formatTime = (time) => {
  const formattedTime = moment(time);
  return formattedTime.format("hh:mm A");
};

// Date + time for admin tables. Admin needs the exact moment a record was
// created, so this is the default for every "Created At"-style column.
export const formatDateTime = (date) => {
  if (!date) return "--";
  return moment(date).format("DD/MM/YYYY hh:mm A");
};
export function stripAllHtmlTags(input) {
  return input?.replace(/<[^>]*>?/gm, "");
}

const options = {
  onDownloadProgress: (progressEvent) => {
    const { loaded, total } = progressEvent;
  },
};
export function downloadFileFromUrl(fileUrl, filename) {
  axios
    .get(fileUrl, {
      responseType: "blob",
      ...options,
    })
    .then(function (response) {
      const url = window.URL.createObjectURL(
        new Blob([response.data], {
          type: response.headers["content-type"],
        })
      );
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
    })
    .catch((e) => {});
}

export const scrollToTop = (topVal = 0) => {
  window.scrollTo({
    top: topVal,
    behavior: "smooth",
  });
};

export const formatNumber = (num) => {
  if (!num && num !== 0) return "";
  if (num === undefined || num === null) return `${CURRENCY}0`;
  if (num % 1 === 0) {
    return `${CURRENCY}${num?.toLocaleString()}`;
  } else {
    return `${CURRENCY}${num?.toFixed(2)?.toLocaleString()}`;
  }
};

export async function convertImageFile(file) {
  if (typeof window !== "undefined") {
    const heic2any = require("heic2any");
    if (file?.type === "image/heic") {
      try {
        const convertedBlob = await heic2any({
          blob: file,
          toType: "image/jpeg",
        });
        return new File(
          [convertedBlob],
          file.name.replace(/\.heic$/i, ".jpg"),
          {
            type: "image/jpeg",
            lastModified: Date.now(),
          }
        );
      } catch (error) {
        console.error("Error converting HEIC to JPEG:", error);
        throw error;
      }
    } else if (file?.type === "image/webp") {
      try {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");
        const img = new Image();
        img.src = URL.createObjectURL(file);
        await new Promise((resolve) => {
          img.onload = resolve;
        });
        canvas.width = img.width;
        canvas.height = img.height;
        ctx.drawImage(img, 0, 0);
        return new Promise((resolve) => {
          canvas.toBlob((blob) => {
            const convertedFile = new File(
              [blob],
              file.name.replace(/\.webp$/i, ".jpg"),
              {
                type: "image/jpeg",
                lastModified: Date.now(),
              }
            );
            resolve(convertedFile);
          }, "image/jpeg");
        });
      } catch (error) {
        console.error("Error converting WebP to JPEG:", error);
        throw error;
      }
    }
    return file;
  }
}
export const handleConvertImages = async (files) => {
  // return files;
  const temp = [];
  for (let i = 0; i < files.length; i++) {
    const file = files[i];
    if (typeof file === "string") {
      temp.push(file);
    } else {
      const image = await convertImageFile(file);
      temp.push(image);
    }
  }
  return temp;
};

export const quillValidateHandler = (
  params,
  toastMessage = "Please fill the home cms field!"
) => {
  let flag = true;
  for (let key in params) {
    const parsedHTML = new DOMParser().parseFromString(
      params[key],
      "text/html"
    );
    const plainText = parsedHTML.body.textContent || "";
    if (plainText?.trim() === "") {
      toast.error(toastMessage);
      flag = false;
      break;
    }
  }
  return flag;
};

export const cn = (...args) => classNames(...args);

export const validateDate = (date) => {
  if (!date) return null; // Return null if date is falsy

  // Check if date is a moment object and has a $d property
  if (date?.$d) {
    return moment(date.$d).format(); // Format if $d exists
  } else {
    return moment(date).format(); // Assume date is a valid input for moment
  }
};

export const normalizeUrl = (url) => {
  if (!url || typeof url !== "string") return "";
  const trimmed = url.trim();
  if (!trimmed) return "";

  if (/^https?:\/\//i.test(trimmed)) {
    return trimmed;
  }

  return `https://${trimmed.replace(/^\/\//, "")}`;
};

export const validateUrl = (url) => {
  if (!url || typeof url !== "string") return false;
  const trimmed = url.trim();
  if (!trimmed) return false;

  try {
    const parsed = new URL(normalizeUrl(trimmed));

    if (!["http:", "https:"].includes(parsed.protocol)) return false;

    const hostname = parsed.hostname;
    if (!hostname) return false;
    if (hostname === "localhost") return true;
    if (!hostname.includes(".")) return false;
    if (!/^[\da-z.-]+$/i.test(hostname)) return false;

    return true;
  } catch {
    return false;
  }
};
export const camelCaseToLower = (str) => {
  return str.replace(formRegEx, formRegExReplacer).toLowerCase();
};

export const urlPattern =
  /^(https?:\/\/)(www\.)?([\w-]+\.)+[a-z]{2,6}(\/[\w-]*)*\/?$/;

export const getYearRange = () => {
  const years = [
    ...Array.from({ length: 5 }).map((_, i) => ({
      label: `${moment().subtract(i, "year").format("YYYY")}`,
      value: `${moment().subtract(i, "year").format("YYYY")}`,
    })),
  ];
  return years;
};

export const RenderToast = ({ type = "error", message = "" }) => {
  let toastId = "error-render-toast";

  if (!type || !message) return;

  toast[type](message, {
    id: toastId,
  });
};

export const validateNestedParams = (
  params,
  parentKey = "",
  options = { disallowEmptyArray: true }
) => {
  const formatKey = (rawKey) => {
    return rawKey
      .replace(/\[(\d+)\]/g, (_, i) => ` ${parseInt(i) + 1}`) // [0] => 1
      .split(".")
      .map(
        (part) =>
          part.replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase()) // capitalize
      )
      .join(" → ");
  };

  if (Array.isArray(params)) {
    if (options.disallowEmptyArray && params.length === 0) {
      toast.error(`${formatKey(parentKey)} cannot be empty`);
      return false;
    }

    for (let i = 0; i < params.length; i++) {
      const key = `${parentKey}[${i}]`;
      if (!validateNestedParams(params[i], key, options)) return false;
    }
  } else if (typeof params === "object" && params !== null) {
    const keys = Object.keys(params);
    if (keys.length === 0 && parentKey) {
      toast.error(`${formatKey(parentKey)} cannot be empty`);
      return false;
    }

    for (const key of keys) {
      const fullKey = parentKey ? `${parentKey}.${key}` : key;
      if (!validateNestedParams(params[key], fullKey, options)) return false;
    }
  } else {
    if (params === "" || params === null || params === undefined) {
      toast.error(`${formatKey(parentKey)} cannot be empty`);
      return false;
    }
  }

  return true;
};

export const validateEmail = (email) => {
  const re =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return re.test(String(email).toLowerCase());
};

export const CreateFormData = (data) => {
  const formData = new FormData();
  for (let key in data) {
    formData.append(key, data[key]);
  }
  return formData;
};

export const getYearOptions = (previousYear = 1950) => {
  const yearOptions = Array.from(
    { length: new Date().getFullYear() - previousYear },
    (_, i) => previousYear + i
  );
  return yearOptions.map((year) => ({
    label: year,
    value: year,
  }));
};

export const getPhoneNumber = (data) => {
  const { dialCode, number } = data || {};
  if (!dialCode && !number) return "";
  if (dialCode && number) {
    return `${dialCode}${number}`;
  } else if (number && !dialCode) {
    return number;
  }
  return "";
};
export const getCountryISOCode = (country) => {
  return Country?.getAllCountries()?.find((item) => item?.name == country)
    ?.isoCode;
};
export const formatNumberWithoutCurrency = (num) => {
  if (num === undefined || num === null) return ``;
  num = parseFloat(num);
  if (num % 1 === 0) {
    return `${num?.toLocaleString()}`;
  } else {
    return `${num?.toFixed(2)?.toLocaleString()}`;
  }
};
export const getPostQuestions = (post, questionTitle = "Questions") => {
  const answers = [];
  if (post?.type === POST_TYPE.PRODUCER) {
    producerQuestions?.forEach?.((item) => {
      answers.push({
        question: item?.question,
        answer: post?.[post?.type]?.[item?.value] ? "Yes" : "No",
      });
    });
  } else {
    consumerQuestions?.forEach?.((item) => {
      answers.push({
        question: item?.question,
        answer: post?.[post?.type]?.[item?.value] ? "Yes" : "No",
      });
    });
  }
  return [{ question: questionTitle, answer: answers }];
};

export const getPostSellerDetails = (
  post,
  questionTitle = "Seller Details"
) => {
  const answers = [];
  answers.push({
    question: "Full Name",
    answer: post?.[post?.type]?.fullName,
  });
  answers.push({
    question: "Email",
    answer: post?.[post?.type]?.email,
  });
  answers.push({
    question: "Phone",
    answer: displayPhoneNumber(post?.[post?.type]?.phone),
  });
  answers.push({
    question: "Link",
    answer: post?.[post?.type]?.link,
    type: "link",
  });
  return [{ question: questionTitle, answer: answers }];
};
export const displayPhoneNumber = (phoneNumber) => {
  if (!phoneNumber) return "";
  return `${phoneNumber?.dialCode} ${phoneNumber?.number}`;
};
export const getId = (id) => {
  return id?.toString()?.slice(-6);
};

export const capitalizeFirstLetter = (string) => {
  const temp = camelCaseToLower(string);
  return temp?.charAt(0)?.toUpperCase() + temp?.slice(1);
};
